'use client';

import * as React from 'react';
import Link, { LinkProps } from 'next/link';
import { usePathname } from 'next/navigation';
import getScrollParent from '@popperjs/core/lib/dom-utils/getScrollParent';

export type IActiveLinkProps = React.DetailedHTMLProps<React.AnchorHTMLAttributes<HTMLAnchorElement>, Element> &
  LinkProps & {
    children: React.ReactNode;
    className?: string;
    exact?: boolean;
    title?: string;
    activeRef?: React.MutableRefObject<HTMLAnchorElement | null>;
    scrollIntoView?: boolean;
  };

const ActiveLink: React.FunctionComponent<IActiveLinkProps> = ({ children, exact, ...props }) => {
  const { activeRef, scrollIntoView, ...rest } = props;
  const r = React.useRef<HTMLAnchorElement>(null);
  const [firstRun, setFirstRun] = React.useState(true);
  const pathname = usePathname();
  const linkPathname = (props.as || props.href) as string;
  const isMatch = exact ? pathname === linkPathname : pathname.startsWith(linkPathname);
  const isExactMatch = pathname === linkPathname;

  React.useEffect(() => {
    if (typeof activeRef !== 'undefined' && isMatch && r.current && props.activeRef) {
      props.activeRef.current = r.current;
    }
  }, [r, isMatch, activeRef, props]);

  React.useEffect(() => {
    if (r.current && isMatch && scrollIntoView) {
      const parent = getScrollParent(r.current);
      const offset = r.current.offsetLeft;
      parent.scrollTo({
        left: offset,
        behavior: firstRun ? 'instant' : 'smooth',
      });
    }
    if (firstRun) {
      setFirstRun(false);
    }
  }, [firstRun, isMatch, scrollIntoView]);

  return (
    <Link
      {...rest}
      aria-current={isMatch}
      draggable={false}
      title={props.title}
      ref={r}
      prefetch={false}
      onClick={(e) => {
        if (isExactMatch) {
          e.preventDefault();
        }
        if (props.onClick) {
          props.onClick(e);
        }
      }}
    >
      {children}
    </Link>
  );
};

ActiveLink.displayName = 'ActiveLink';

export default ActiveLink;
